import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../services/api';
import { toast } from 'react-toastify';

function Payment() {
  const [step, setStep] = useState(1);
  const [gateways, setGateways] = useState([]);
  const [selectedGateway, setSelectedGateway] = useState('');
  const [qrData, setQrData] = useState(null);
  const [utrId, setUtrId] = useState('');
  const [loading, setLoading] = useState(false);
  const [joiningAmount, setJoiningAmount] = useState(null);
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth || {});

  useEffect(() => {
    fetchGateways();
    checkStatus();
    fetchAmount();
  }, []);

  useEffect(() => {
    if (step !== 3) return;
    const interval = setInterval(checkStatus, 8000);
    return () => clearInterval(interval);
  }, [step]);

  const fetchGateways = async () => {
    try {
      const res = await api.get('/Payment/gateways');
      setGateways(res.data.data || []);
      if (res.data.data?.length > 0) {
        setSelectedGateway(res.data.data[0].gatewayName);
      }
    } catch (err) {
      console.log(err);
    }
  };

  // Plan A / Plan B amount from config + user.plan
  const fetchAmount = async () => {
    try {
      const res = await api.get('/AdminConfig/current');
      const cfg = res.data.data || {};
      const plan = (user?.plan || 'A').toUpperCase();
      const amount =
        plan === 'B'
          ? cfg.planBJoiningAmount ?? 10000
          : cfg.joiningAmount ?? 3100;
      setJoiningAmount(Number(amount));
    } catch (err) {
      const plan = (user?.plan || 'A').toUpperCase();
      setJoiningAmount(plan === 'B' ? 10000 : 3100);
    }
  };

  const checkStatus = async () => {
    try {
      const res = await api.get('/Payment/status');
      const data = res.data.data;

      // Pending payment का real amount UI पर
      if (data?.amount != null) {
        setJoiningAmount(Number(data.amount));
      }

      if (data?.isActive || data?.status === 'Completed') {
        toast.success('Payment Approved! Redirecting...');
        setTimeout(() => navigate('/dashboard'), 1500);
        return;
      }

      try {
        const ekqrRes = await api.get('/Payment/ekqr/status');
        if (ekqrRes.data.data?.isActive) {
          toast.success('ekqr Payment Success!');
          setTimeout(() => navigate('/dashboard'), 1500);
        }
      } catch (e) {
        // ignore
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleGenerateQr = async () => {
    if (!selectedGateway) {
      toast.error('Please select a gateway');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/Payment/generate-qr', {
        gatewayName: selectedGateway,
      });
      const data = res.data.data;
      setQrData(data);
      if (data?.amount != null) setJoiningAmount(Number(data.amount));
      setStep(2);
      toast.success('QR Code generated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate QR');
    } finally {
      setLoading(false);
    }
  };

  const handleEkqrPay = async () => {
    setLoading(true);
    try {
      const res = await api.post('/Payment/ekqr/create');
      const data = res.data.data;

      if (data?.amount != null) setJoiningAmount(Number(data.amount));

      if (data.paymentUrl) {
        window.location.href = data.paymentUrl;
        return;
      }

      setQrData(data);
      setStep(2);
      toast.success('Order created!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUtr = async () => {
    if (!utrId || utrId.length < 5) {
      toast.error('Valid UTR ID enter करें');
      return;
    }
    setLoading(true);
    try {
      await api.post('/Payment/submit-utr', { utrId });
      toast.success('UTR submitted! Waiting for Admin approval');
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePayClick = () => {
    if (selectedGateway === 'ekqr') {
      handleEkqrPay();
    } else {
      handleGenerateQr();
    }
  };

  const amountLabel =
    joiningAmount != null
      ? `₹${Number(joiningAmount).toLocaleString('en-IN')}`
      : '₹...';

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-700 to-blue-900 flex items-center justify-center px-4 py-8">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
        <h2 className="text-2xl font-bold text-center text-blue-700 mb-1">
          Payment {amountLabel}
        </h2>
        <p className="text-center text-gray-500 text-sm mb-1">Joining Amount</p>
        {user?.plan && (
          <p className="text-center text-xs text-blue-600 mb-6">
            Plan {user.plan}
          </p>
        )}
        {!user?.plan && <div className="mb-6" />}

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm font-medium text-gray-700">Select Payment Method:</p>

            {gateways.map((g) => (
              <button
                key={g.id}
                onClick={() => setSelectedGateway(g.gatewayName)}
                className={`w-full p-4 rounded-xl border-2 text-left transition ${
                  selectedGateway === g.gatewayName
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200'
                }`}
              >
                <p className="font-bold">{g.gatewayName}</p>
                <p className="text-sm text-gray-500">{g.upiId}</p>
              </button>
            ))}

            <button
              onClick={() => setSelectedGateway('ekqr')}
              className={`w-full p-4 rounded-xl border-2 text-left transition ${
                selectedGateway === 'ekqr'
                  ? 'border-green-600 bg-green-50'
                  : 'border-gray-200'
              }`}
            >
              <p className="font-bold">ekqr (UPI Gateway)</p>
              <p className="text-sm text-gray-500">Auto payment via ekqr</p>
            </button>

            <button
              onClick={handlePayClick}
              disabled={loading || !selectedGateway}
              className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl disabled:opacity-50"
            >
              {loading
                ? 'Please wait...'
                : selectedGateway === 'ekqr'
                ? 'Pay with ekqr'
                : 'Generate QR Code'}
            </button>
          </div>
        )}

        {step === 2 && qrData && (
          <div className="space-y-4">
            {qrData.qrCodeUrl && (
              <div className="text-center">
                <img
                  src={qrData.qrCodeUrl}
                  alt="QR Code"
                  className="w-56 h-56 mx-auto rounded-xl border-4 border-blue-100"
                />
                <p className="text-sm text-gray-500 mt-2">
                  Scan & Pay {amountLabel}
                </p>
              </div>
            )}

            {qrData.upiId && (
              <div className="bg-gray-50 p-3 rounded-xl text-sm">
                <p className="text-gray-500">UPI ID</p>
                <p className="font-mono font-bold">{qrData.upiId}</p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-700">Enter UTR ID</label>
              <input
                type="text"
                value={utrId}
                onChange={(e) => setUtrId(e.target.value.toUpperCase())}
                placeholder="e.g. 403510051186"
                className="w-full mt-1 border rounded-xl px-4 py-3 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              onClick={handleSubmitUtr}
              disabled={loading || utrId.length < 5}
              className="w-full bg-green-600 text-white font-bold py-3 rounded-xl disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit UTR'}
            </button>

            <button
              onClick={() => setStep(1)}
              className="w-full text-sm text-gray-500 py-2"
            >
              ← Change Gateway
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="text-center py-8 space-y-4">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <h3 className="text-xl font-bold text-gray-800">Waiting for Approval</h3>
            <p className="text-gray-500 text-sm">
              UTR: <span className="font-mono font-bold">{utrId}</span>
            </p>
            <p className="text-xs text-blue-500">Auto checking status...</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Payment;